import { ClockCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import type { AlignType } from '@rc-component/table/lib/interface';
import { Table, Typography } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { BillingTypeWithBadge } from '../../../components/BillingTypeWithBadge.tsx';
import { Price } from '../../../components/Price.tsx';
import { deCollator } from '../../../utils/collator.ts';
import type { Order, OrderParameter } from './useRequestDetailsQuery.tsx';

export const ParametersTab = ({ order, showPrices, flipParamNames }: { order: Order; showPrices: boolean; flipParamNames: boolean }) => {
  const hasAcuteParams = order.orderParameters.some(it => it.acute) ?? false;

  const longNameStyle = css`
    display: inline-block;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  `;

  const shortNameCol: ColumnType<OrderParameter> = {
    title: 'Kurzname',
    dataIndex: 'shortName',
    key: 'shortName',
    defaultSortOrder: flipParamNames ? undefined : 'ascend',
    sorter: (a, b) => deCollator.compare(a.shortName, b.shortName),
    render: v => (
      <Typography.Text className={monoFont} style={{ fontWeight: flipParamNames ? 'normal' : 500 }}>
        {v}
      </Typography.Text>
    ),
  };

  const longNameCol: ColumnType<OrderParameter> = {
    title: 'Langbezeichnung',
    dataIndex: 'longName',
    key: 'longName',
    defaultSortOrder: flipParamNames ? 'ascend' : undefined,
    sorter: (a, b) => a.longName.localeCompare(b.longName),
    // the one column of free text, capped so it does not decide the width of the table
    render: v => (
      <span className={longNameStyle} style={{ fontWeight: flipParamNames ? 500 : 'normal' }} title={v}>
        {v}
      </span>
    ),
  };

  const paramColumns: ColumnsType<OrderParameter> = [
    ...(flipParamNames ? [longNameCol, shortNameCol] : [shortNameCol, longNameCol]),
    {
      title: 'Verrechnung',
      dataIndex: 'billingType',
      key: 'billingType',
      sorter: (a, b) => a.billingType.localeCompare(b.billingType),
      render: value => <BillingTypeWithBadge billingType={value} />,
    },
    ...(showPrices
      ? [
          {
            title: 'Preis',
            dataIndex: 'price',
            key: 'price',
            sorter: (a, b) => (a.price ?? -1) - (b.price ?? -1),
            render: value => <Price value={value} />,
          } as ColumnType<OrderParameter>,
        ]
      : []),
    ...(hasAcuteParams && order.acute
      ? [
          {
            title: 'Akut',
            dataIndex: 'acute',
            key: 'acute',
            align: 'center' as AlignType,
            sorter: (a, b) => Number(a.acute) - Number(b.acute),
            render: (_, record) => {
              return record.acute ? (
                <ClockCircleOutlined
                  className={css`
                    font-size: 16px;
                  `}
                />
              ) : (
                ''
              );
            },
          } as ColumnType<OrderParameter>,
        ]
      : []),
    {
      title: 'Sondertarif',
      dataIndex: 'specialRateShortName',
      key: 'specialRateShortName',
      sorter: (a, b) => a.specialRateShortName.localeCompare(b.specialRateShortName),
    },
  ];

  return (
    <Table<OrderParameter>
      // columns as wide as their content, like the labels tab
      tableLayout="auto"
      scroll={{ y: '300px', x: 'max-content' }}
      rowKey={param => param.id}
      size="small"
      showSorterTooltip={false}
      dataSource={order.orderParameters}
      pagination={false}
      columns={paramColumns}
    />
  );
};
